// Angular
import { Injectable } from '@angular/core';

// Rxjs
import { map, Observable, switchMap } from 'rxjs';

// Utils
import { SoledadUtils, Base64 } from '../../lib/helpers/utils';
import { Md5 } from 'ts-md5/dist/md5';

// Models
import { User } from '../../models/b2b/user';
import { NewUser } from '../../models/b2b/newUser';
import { Credentials, CredentialsHelper } from '../../models/b2b/credentials';
import { RememberPass, RememberPassHelper } from '../../models/b2b/rememberPass';

// Enviroment
import { env } from '../../../../../env';

// Services
import { SoledadUserService } from './user.service';
import { ApiService } from '../api/api.service';

// Store
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';

// Actions
import * as RouterActions from '../../../store/router/router.actions';

// Moment
import * as moment from 'moment';
//Lodash
import * as _ from 'lodash';

@Injectable()
export class SoledadAuthenticationService {
    private cred: Credentials;
    private cred2: Credentials;
    private user: User;
    private authString: string;
    private authStringExtras: string;
    private urlRedirect: string;

    constructor(
        private api: ApiService,
        private soledadUserService: SoledadUserService,
        private store: Store<AppState>
    ) {
        this.api.init(env);
    }

    /**
     * Obtain a token for a user if pass validation
     * @param {Credentials} cred Credentinals of user
     * @return Observable<string> Obtained token
     */
    getToken(cred: Credentials): Observable<string> {
        this.cred2 = _.cloneDeep(cred);
        this.authString = SoledadUtils.getAuthString(
            cred.email,
            cred.password,
            'soledad',
            this.api.environment.publicKey
        );
        this.authStringExtras = SoledadUtils.getAuthString(
            cred.email,
            cred.password,
            'confortauto',
            this.api.environment.publicKeyExtras
        );
        this.urlRedirect = SoledadUtils.getUrlRedirectString(
            cred.email,
            cred.password,
            'confortauto',
            this.api.environment.publicKeyExtras,
            cred.rememberLoginChecked
        );

        const params = {
            authString: this.authString,
            userCode: this.api.environment.userCode
        };

        // Authenticate call
        return this.api.callSoledad(null, params).pipe(
            map(
                (res) => {
                    if (res.result === 0) {
                        this.saveToken(res.value, this.authStringExtras, this.urlRedirect, Md5.hashStr(cred.email));

                        if (sessionStorage.getItem('remember') || cred.auto || cred.rememberLoginChecked) {
                            if (this.cred2.email !== 'resetPass' || cred.rememberLoginChecked) {
                                this.saveAccessDataLocalStorage(this.cred2);
                            }
                            this.saveTokenLocalStorage(res.value, this.authStringExtras, this.urlRedirect);
                            sessionStorage.removeItem('remember');
                        } else {
                            localStorage.removeItem('soledad.accessData');
                        }
                        return res.value;
                    } else {
                        return '';
                    }
                },
                (error) => {
                    console.log('error', error);
                    return '';
                }
            )
        );
    }

    saveAccessDataLocalStorage(cred2: Credentials): void {
        const cred: any = {
            login: cred2.email,
            password: cred2.password
        };

        const encode: string = Base64.encode(
            SoledadUtils.cifrarXor(JSON.stringify(cred), this.api.environment.publicKey)
        );
        localStorage.setItem('soledad.accessData', encode);
    }

    /**
     * Log a user
     * @param cred
     */
    login(cred: Credentials): Observable<boolean> {
        // Last credentials used
        this.cred = cred;
        // Authenticate call
        return this.getToken(cred).pipe(
            map((token) => {
                return token.length > 0;
            })
        );
    }

    /**
     *  Encrypt a credential object
     * @param {Credentials} cred  The cred
     *
     * @return encrypted credentials
     */
    encryptCredentials(cred: Credentials): string {
        return Base64.encode(SoledadUtils.cifrarXor(JSON.stringify(cred), this.api.environment.publicKey));
    }

    /**
     * @param  {string} encryptedCredentials encryptedCredentials The encrypted credentials
     * @return {Credentials} Credentials decrypted
     */
    decryptCredentials(encryptedCredentials: string): Credentials {
        const aux = SoledadUtils.cifrarXor(Base64.decode(encryptedCredentials), this.api.environment.publicKey);
        return CredentialsHelper.newFromJSON(aux);
    }

    /**
     * Logout the user, updates observable activeUser
     * @return     void
     */
    logout(): void {
        this.removeToken();

        this.removeSessionStorage();

        // Redirect
        this.store.dispatch(
            new RouterActions.Go({
                path: ['/login']
            })
        );
    }

    redirectToVersion(user: User, version: string): void {
        let url = window.location.href;

        if (!env['local'] && url.indexOf('current') == -1 && url.indexOf('new') == -1) {
            return;
        }

        url = url.replace(/current/gi, '{redirect-version}');
        url = url.replace(/new/gi, '{redirect-version}');
        url = url.replace(/{redirect-version}/, version);

        if (env['local']) {
            const replace = version === 'current' ? '4300' : '4200';
            url = url.replace('4300', '{redirect-version}');
            url = url.replace('4200', '{redirect-version}');
            url = url.replace(/{redirect-version}/, replace);
        }
        // Si exists new-version redirect to user generic urls
        if (url.search('{redirect-version') >= 0) {
            url = version === 'current' ? user.urlNew : user.urlCurrent;
        }

        // Clean from index.html
        // En el momento en que current sea la versión de restyling se tendrá que dejar solo la versión con #
        url = version === 'current' ? url.replace(/dashboard.*$/gi, '') : url.replace(/dashboard.*$/gi, '#');

        let urlRedirect = url + '?params=' + this.soledadUserService.getUrlRedirectFromStorage();

        window.location.href = urlRedirect;
    }

    private getRedirectUrl(): string {
        const localUrl = window.location;
        const redirect = env.redirecciones.filter((item) => localUrl.href.includes(item.origin));

        if (redirect.length > 0) {
            const urlToRedirect =
                redirect[0].destination + '?auto=1&code=' + this.soledadUserService.getUrlRedirectFromStorage();
            return urlToRedirect;
        }

        return '';
    }

    /**
     * Check for version redirection
     */
    checkRedirection = function (user: User) {
        const redirectUrl = this.getRedirectUrl();
        if (redirectUrl !== '' && user.redireccionaUrl) {
            if (user.mostrarMensajeNuevaUrl) {
                this.ms.open('alert-redirect', { disableAutoClose: true }, { redirectUrl }).subscribe((data) => {
                    if (data === undefined) {
                        this.redirect(redirectUrl);
                    }
                });
            } else {
                this.redirect(redirectUrl);
            }
        } else {
            let versionOfUser = user.versionb2b;

            //############# todo quitar estas línea
            // versionOfUser = 'new';
            // let versionOfUrl = 'current';
            //#############

            let versionOfUrl = '';
            if (!env['local']) {
                versionOfUrl = window.location.href.search('current') >= 0 ? 'current' : 'new';
            } else {
                versionOfUrl = window.location.href.search(':4300') >= 0 ? 'current' : 'new';
            }

            if (env.redirect && versionOfUser !== versionOfUrl && versionOfUser != '') {
                this.redirectToVersion(user, versionOfUser);
            }
        }
    };

    /**
     * Check if user is logged
     *
     * @returns {boolean}
     *
     * @memberOf SoledadAuthenticationService
     */
    isLoggedIn(): boolean {
        if (this.getAuthToken()) {
            this.manageUuidToken();
            return true;
        }
        // console.log('isLoggedOut');
        return false;
    }

    private getAuthToken(): string | null {
        return sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
    }

    /**
     * Save token in sessionStorage
     *
     * @param string authToken
     * @param string authTokenExtras
     *
     * @memberOf SoledadAuthenticationService
     */
    saveToken(authToken: string, authTokenExtras: string, urlRedirect: string, userMd5: any): void {
        sessionStorage.setItem('authToken', authToken);
        sessionStorage.setItem('authTokenExtras', authTokenExtras);
        sessionStorage.setItem('urlRedirectToken', urlRedirect);
        sessionStorage.setItem('userMd5', userMd5);
    }

    /**
     * Save token in sessionStorage
     *
     * @param string authToken
     * @param string authTokenExtras
     *
     * @memberOf SoledadAuthenticationService
     */
    saveTokenLocalStorage(authToken: string, authTokenExtras: string, urlRedirect: string): void {
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('authTokenExtras', authTokenExtras);
        localStorage.setItem('urlRedirectToken', urlRedirect);
    }

    /**
     * Removes token for sessionStorage
     *
     * @memberOf SoledadAuthenticationService
     */
    removeToken(): void {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authTokenExtras');
        localStorage.removeItem('authToken');
        localStorage.removeItem('authTokenExtras');

        localStorage.removeItem('uuid');
    }

    removeSessionStorage(removeAccesData = true): void {
        sessionStorage.clear();
        this.removeLocalStorare(removeAccesData);
    }

    private removeLocalStorare(removeAccessData = true): void {
        localStorage.removeItem('tokenOpinions');
        for (let key in localStorage) {
            if (key.indexOf('soledad.accessData') >= 0) {
                if (removeAccessData && localStorage.getItem(key)) {
                    localStorage.removeItem(key);
                }
                continue;
            }
            if (key.indexOf('cn_') < 0 && key.indexOf('cart') < 0 && localStorage.getItem(key)) {
                localStorage.removeItem(key);
            } else if (key.indexOf('cart') >= 0 && key.indexOf('cn_') < 0 && localStorage.getItem(key)) {
                localStorage.removeItem(key);
            }
        }
    }

    /**
     * Request for new password
     *
     * @param {string} username Username
     * @param {string} returnPath Return path
     * @return {Observable<RememberPass>} Observable subscribe
     *
     * @memberOf SoledadUserService
     */
    getNewPassword(username: string, returnPath: string): Observable<RememberPass> {
        const params = [];
        params['USR'] = username;
        params['URL'] = Base64.encode(returnPath);

        const cred = CredentialsHelper.create();
        cred.email = 'resetPass';
        cred.password = 'rPeass';

        return this.getToken(cred).pipe(
            switchMap((token) => this.api.callSoledad('b2bNew.dbo.HTML_UsuariosSolicitudRestaurarContrasena', params)),
            map((res) => {
                // console.log('HTML_UsuariosSolicitudRestaurarContrasena', {res});

                this.removeToken();

                if (res && res.result === 0 && res.value && res.value.length > 0) {
                    return res.value.map((row) => RememberPassHelper.transformFromApi(row));
                }
                return [];
            })
        );
    }

    /**
     * New user register
     * @param newUser
     */
    newUser(newUser: NewUser): Observable<boolean> {
        const params = [];
        params['RAZON_SOCIAL'] = newUser.social;
        params['NOMBRE_COMERCIAL'] = newUser.name;
        params['PERSONADECONTACTO'] = newUser.contact_name;
        params['CIF'] = newUser.cif;
        params['TELEFONO'] = newUser.phone;
        params['CORREOELECTRONICO'] = newUser.email;
        params['DIRECION'] = newUser.address;
        params['LOCALIDAD'] = newUser.location;
        params['CP'] = newUser.code;
        params['PROVINCIA'] = newUser.province;
        // params['URL'] = newUser.url;

        const cred = CredentialsHelper.create();
        cred.email = 'resetPass';
        cred.password = 'rPeass';

        return this.getToken(cred).pipe(
            switchMap((token) => this.api.callSoledad('b2bNew.dbo.HTML_EmailNuevosClientes', params)),
            map((res) => {
                // console.log('HTML_EmailNuevosClientes', {res});

                this.removeToken();

                if (res && res.result === 0 && !res.value.Rows) {
                    return true;
                }
                return false;
            })
        );
    }

    /**
     * Reset password
     *
     * @param {any} session
     * @param {any} password
     * @param {any} callback
     *
     * @memberOf SoledadUserService
     */
    resetPassword(session: string, password: string): Observable<any> {
        // Obtain token
        const cred = CredentialsHelper.create();
        cred.email = 'resetPass';
        cred.password = 'rPeass';

        const params = [];
        params['SESION'] = session;
        params['TIPO_OPERACION'] = 2;
        params['CONTRASENA_NUEVA'] = password;

        return this.getToken(cred).pipe(
            switchMap((token) =>
                this.api.callSoledad('b2bNew.dbo.HTML_UsuariosConfirmacionRestaurarContrasena', params)
            ),
            map((res) => {
                // console.log('HTML_UsuariosConfirmacionRestaurarContrasena', {res});

                this.removeToken();

                if (res && res.result === 0 && res.value && res.value.length > 0 && !res.value[0].ERROR) {
                    return res;
                }
                return [];
            })
        );
    }

    getPublicKeyExtras(): string {
        return this.api.environment.publicKeyExtras;
    }

    getAllowedEntities(): any {
        return this.api.environment.allowedEntities;
    }

    manageUuidToken(): void {
        const localUuid = localStorage.getItem('uuid');
        const token = this.getAuthToken();

        if (!localUuid) {
            this.generateUuidToken(token);
        }
    }

    regenerateUuidToken() {
        const token = this.getAuthToken();
        this.generateUuidToken(token);
    }

    generateUuidToken(token: string = ''): void {
        const uuid = [];
        uuid[0] = ('' + Md5.hashStr(moment().unix() + token + token)).substring(0, 10);
        uuid[1] = ('' + Md5.hashStr(moment().unix() + token)).substring(0, 10);
        uuid[2] = ('' + Md5.hashStr(token + token + moment().unix())).substring(0, 10);
        uuid[3] = ('' + Md5.hashStr(token + moment().unix())).substring(0, 10);

        localStorage.setItem('uuid', uuid.join('-'));
    }
}
